/**
 * CancelSubscriptionSection Component
 *
 * Displays subscription cancellation status and provides cancel/reactivate actions.
 * - If active: Shows "Cancel Subscription" button with confirmation dialog
 * - If cancel_at_period_end: Shows warning banner + "Keep Subscription" button
 */

import { useState } from 'react';
import { Error as ErrorIcon, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { useEventTracking } from '@services/statsig';

import { useCancelSubscriptionMutation, useReactivateSubscriptionMutation } from '../store/api';

interface CancelSubscriptionSectionProps {
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | number | null;
  subscriptionStatus?: string | null;
  onStatusChange?: () => void;
}

// Format date (handles both ISO string and unix timestamp)
const formatDate = (dateValue: string | number | null | undefined): string => {
  if (!dateValue) return 'N/A';
  // If it's a number (Unix timestamp), convert from seconds to milliseconds
  const date = typeof dateValue === 'number' ? new Date(dateValue * 1000) : new Date(dateValue);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export function CancelSubscriptionSection({
  cancelAtPeriodEnd,
  currentPeriodEnd,
  subscriptionStatus,
  onStatusChange,
}: CancelSubscriptionSectionProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { subscriptionManagement } = useEventTracking();

  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivating }] =
    useReactivateSubscriptionMutation();

  // Don't show section if no subscription or already canceled (not just set to cancel)
  if (!subscriptionStatus || subscriptionStatus === 'canceled') {
    return null;
  }

  const handleCancelClick = () => {
    subscriptionManagement.cancelClicked();
    setShowCancelDialog(true);
    setError(null);
  };

  const handleCloseCancelDialog = () => {
    setShowCancelDialog(false);
    setCancellationReason('');
    setError(null);
  };

  const handleConfirmCancel = async () => {
    setError(null);
    try {
      await cancelSubscription({
        cancelSubscriptionRequest: {
          cancellation_reason: cancellationReason || null,
        },
      }).unwrap();
      subscriptionManagement.cancelConfirmed({
        cancellation_reason_provided: !!cancellationReason.trim(),
      });
      handleCloseCancelDialog();
      onStatusChange?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to cancel subscription. Please try again.';
      setError(errorMessage);
    }
  };

  const handleReactivate = async () => {
    setError(null);
    subscriptionManagement.reactivateClicked();
    try {
      await reactivateSubscription().unwrap();
      subscriptionManagement.reactivateConfirmed();
      onStatusChange?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to reactivate subscription. Please try again.';
      setError(errorMessage);
    }
  };

  // Show reactivation UI if subscription is set to cancel at period end
  if (cancelAtPeriodEnd) {
    return (
      <Box>
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleReactivate}
              disabled={isReactivating}
              sx={{ fontWeight: 600 }}
            >
              {isReactivating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                'Keep Subscription'
              )}
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Your subscription is scheduled to cancel
          </Typography>
          <Typography variant="body2">
            You will lose access on {formatDate(currentPeriodEnd)}. Click &quot;Keep
            Subscription&quot; to continue your plan.
          </Typography>
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  // Show cancel subscription UI for active subscriptions
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'error.main',
        bgcolor: theme => `${theme.palette.error.main}08`,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main', mb: 1 }}>
        Danger Zone
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        If you cancel, you&apos;ll continue to have access until the end of your current billing
        period ({formatDate(currentPeriodEnd)}).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="outlined"
        color="error"
        onClick={handleCancelClick}
        disabled={isCanceling}
        startIcon={isCanceling ? <CircularProgress size={16} /> : undefined}
      >
        Cancel Subscription
      </Button>

      {/* Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Are you sure you want to cancel your subscription? You will continue to have access
            until <strong>{formatDate(currentPeriodEnd)}</strong>, after which you will lose access
            to premium features.
          </DialogContentText>
          <TextField
            label="Reason for canceling (optional)"
            multiline
            rows={3}
            fullWidth
            value={cancellationReason}
            onChange={e => setCancellationReason(e.target.value)}
            placeholder="We'd love to know why you're leaving so we can improve..."
            variant="outlined"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCancelDialog} disabled={isCanceling}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={isCanceling}
            startIcon={isCanceling ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isCanceling ? 'Canceling...' : 'Yes, Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
